/* Admin sign-in — Firebase email/password auth. */
import { useEffect, useState } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../lib/firebase.js";
import { Link, navigate } from "../../lib/router-compat.jsx";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { Button, Field, Input, Card, Icon, BoltDeco, BrandMark } from "../../ui/index.js";

export default function AdminSignInPage() {
  const { user, profile, loading: authLoading } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | forgot | forgotSent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Already authorised → bounce to dashboard.
  useEffect(() => {
    if (!authLoading && user && profile) navigate("/admin", { replace: true });
  }, [authLoading, user, profile]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/user-not-found"
        ? "Incorrect email or password."
        : err.message);
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMode("forgotSent");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin admin-sign-in-shell" style={{ minHeight: "100vh", background: "var(--bg-warm)", display: "grid", placeItems: "center", padding: 24, position: "relative", overflow: "hidden" }}>
      <BoltDeco size={400} color="rgba(233, 30, 99, 0.08)" style={{ position: "absolute", top: -80, left: -80 }} />
      <BoltDeco size={300} color="rgba(244, 180, 0, 0.12)" style={{ position: "absolute", bottom: -60, right: -40 }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 400 }}>
        <Card variant="elevated" style={{ padding: 32, boxShadow: "var(--shadow-lg)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <BrandMark size={48} />
            <h1 className="t-2xl" style={{ margin: "16px 0 0" }}>Admin sign in</h1>
            <p className="t-sm muted" style={{ margin: "4px 0 0" }}>Purple Angels Kids Foundation</p>
          </div>

          {mode === "signin" && (
            <form onSubmit={submit} className="stack-lg" noValidate>
              <Field label="Email address" htmlFor="signin-email">
                <Input id="signin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@purpleanglekids.org" />
              </Field>
              <Field label="Password" htmlFor="signin-pw">
                <div style={{ position: "relative" }}>
                  <Input id="signin-pw" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPw((p) => !p)} aria-label={showPw ? "Hide password" : "Show password"} aria-pressed={showPw} style={{ position: "absolute", right: 6, top: 6, width: 28, height: 28, display: "grid", placeItems: "center", border: 0, background: "transparent", color: "var(--text-muted)", borderRadius: 6, cursor: "pointer" }}>
                    {showPw ? <Icon.EyeOff size={16} /> : <Icon.Eye size={16} />}
                  </button>
                </div>
              </Field>
              <div style={{ textAlign: "right", marginTop: -8 }}>
                <button type="button" onClick={() => setMode("forgot")} style={{ background: "transparent", border: 0, color: "var(--primary)", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Forgot password?</button>
              </div>
              {error && (
                <div className="field__error" role="alert" style={{ fontSize: 14, padding: 12, background: "var(--danger-bg)", borderRadius: 8 }}>
                  <Icon.AlertCircle size={14} />{error}
                </div>
              )}
              <Button type="submit" variant="primary" size="lg" loading={loading} className="btn--full">{loading ? "Signing in..." : "Sign in"}</Button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={sendReset} className="stack-lg" noValidate>
              <p className="t-sm muted">Enter your admin email and we'll send a reset link.</p>
              <Field label="Email address"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@purpleanglekids.org" /></Field>
              {error && <div className="field__error" role="alert"><Icon.AlertCircle size={14} />{error}</div>}
              <div className="row" style={{ justifyContent: "space-between" }}>
                <Button type="button" variant="ghost" onClick={() => setMode("signin")}>← Back</Button>
                <Button type="submit" variant="primary" loading={loading}>{loading ? "Sending..." : "Send reset link"}</Button>
              </div>
            </form>
          )}

          {mode === "forgotSent" && (
            <div className="stack-lg" style={{ textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--success-bg)", color: "var(--success)", display: "grid", placeItems: "center", margin: "0 auto" }}><Icon.Mail size={24} /></div>
              <p className="t-base">If an account exists for that email, a reset link is on the way.</p>
              <Button variant="secondary" onClick={() => setMode("signin")}>Back to sign in</Button>
            </div>
          )}
        </Card>
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Link to="/" className="t-sm muted">← Back to public site</Link>
        </div>
      </div>
    </div>
  );
}
